import React, { useState, useEffect } from 'react';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { uploadReceipt, downloadReceipt, deleteReceipt } from '../utils/declaratiesS3';

const MAANDEN = ['januari','februari','maart','april','mei','juni','juli','augustus','september','oktober','november','december'];

const CATEGORIEEN = ['OV', 'Auto', 'Parkeren', 'Taxi / Uber', 'Lunch / Diner', 'Hotel', 'Overig'];

function Declaraties({ month, year, userId, userProfile, ovBedragen = {}, ovBonnetjes = {} }) {
  const storageKey = `${userId}_declaraties_${year}-${String(month + 1).padStart(2, '0')}`;
  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;

  const [declaraties, setDeclaraties] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey)) || []; }
    catch { return []; }
  });

  const [toonFormulier, setToonFormulier] = useState(false);
  const [datum, setDatum] = useState('');
  const [categorie, setCategorie] = useState('OV');
  const [omschrijvingOverig, setOmschrijvingOverig] = useState('');
  const [bedrag, setBedrag] = useState('');
  const [bonnetje, setBonnetje] = useState(null);
  const [uploadBezig, setUploadBezig] = useState(false);
  const [exportBezig, setExportBezig] = useState(false);
  const [copyToast, setCopyToast] = useState(false);
  const [verwijderBezig, setVerwijderBezig] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewNaam, setPreviewNaam] = useState('');

  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    setDeclaraties(saved ? JSON.parse(saved) : []);
  }, [storageKey]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(declaraties));
  }, [declaraties, storageKey]);

  const vandaagInMaand = () => {
    const vandaag = new Date();
    const inMaand = vandaag.getFullYear() === year && vandaag.getMonth() === month;
    if (inMaand) return vandaag.toISOString().split('T')[0];
    return new Date(year, month, 1).toISOString().split('T')[0];
  };

  const openFormulier = () => {
    setDatum(vandaagInMaand());
    setCategorie('OV');
    setOmschrijvingOverig('');
    setBedrag('');
    setBonnetje(null);
    setToonFormulier(true);
  };

  const sluitFormulier = () => {
    setToonFormulier(false);
    setBonnetje(null);
    setOmschrijvingOverig('');
  };

  const omschrijving = categorie === 'Overig' ? omschrijvingOverig : categorie;

  const handleVoegToe = async (e) => {
    e.preventDefault();
    if (!omschrijving.trim() || !bedrag) return;
    setUploadBezig(true);
    try {
      const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      let bonnetjeKey = null, bonnetjeNaam = null, bonnetjeType = null;
      if (bonnetje) {
        bonnetjeKey = await uploadReceipt(userId, year, month, id, bonnetje);
        bonnetjeNaam = bonnetje.name;
        bonnetjeType = bonnetje.type;
      }
      const nieuw = {
        id,
        datum,
        categorie: categorie === 'Overig' ? 'Overig' : categorie,
        omschrijving: omschrijving.trim(),
        bedrag: parseFloat(bedrag.replace(',', '.')),
        bonnetjeKey,
        bonnetjeNaam,
        bonnetjeType,
      };
      setDeclaraties(prev => [...prev, nieuw].sort((a, b) => a.datum.localeCompare(b.datum)));
      sluitFormulier();
    } catch (err) {
      alert('Uploaden mislukt: ' + err.message);
    } finally {
      setUploadBezig(false);
    }
  };

  const handleVerwijder = async (d) => {
    if (!window.confirm(`Verwijder "${d.omschrijving}"?`)) return;
    setVerwijderBezig(d.id);
    try {
      if (d.bonnetjeKey) await deleteReceipt(d.bonnetjeKey);
      setDeclaraties(prev => prev.filter(x => x.id !== d.id));
    } catch (err) {
      alert('Verwijderen mislukt: ' + err.message);
    } finally {
      setVerwijderBezig(null);
    }
  };

  const handleBekijkBonnetje = async (d) => {
    if (!d.bonnetjeKey) return;
    try {
      const blob = await downloadReceipt(d.bonnetjeKey);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewNaam(d.bonnetjeNaam || 'bonnetje');
    } catch (err) {
      alert('Bonnetje kon niet worden geopend: ' + err.message);
    }
  };

  const sluitPreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const handleKopieerTabel = () => {
    const regels = declaraties.map(d =>
      `${formatDatum(d.datum)}\t${d.omschrijving}\t€ ${parseFloat(d.bedrag).toFixed(2).replace('.', ',')}`
    );
    ovRegels.forEach(r => {
      if (r.bedrag > 0) regels.push(`${formatDatum(r.dateString)}\tOV (registratie)\t€ ${parseFloat(r.bedrag).toFixed(2).replace('.', ',')}`);
    });
    regels.sort();
    regels.push(`\t\tTotaal\t€ ${(totaal + ovTotaal).toFixed(2).replace('.', ',')}`);
    navigator.clipboard.writeText([`Declaraties ${MAANDEN[month]} ${year}`, '', ...regels].join('\n'));
    setCopyToast(true);
    setTimeout(() => setCopyToast(false), 2000);
  };

  const handleExportPDF = async () => {
    setExportBezig(true);
    try {
      const pdfDoc = await PDFDocument.create();
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      const pagina = pdfDoc.addPage([595, 842]);
      const { width, height } = pagina.getSize();
      const maand = MAANDEN[month];
      const naam = [userProfile?.voornaam, userProfile?.achternaam].filter(Boolean).join(' ');

      pagina.drawRectangle({ x: 0, y: height - 80, width, height: 80, color: rgb(0.09, 0.21, 0.37) });
      pagina.drawText('Track My Day', { x: 40, y: height - 28, size: 10, font, color: rgb(0.58, 0.77, 0.99) });
      pagina.drawText(`Declaraties ${maand} ${year}`, { x: 40, y: height - 52, size: 18, font: fontBold, color: rgb(1, 1, 1) });
      if (naam) pagina.drawText(naam, { x: 40, y: height - 100, size: 11, font, color: rgb(0.4, 0.4, 0.4) });

      let y = height - 140;
      pagina.drawRectangle({ x: 40, y: y - 4, width: width - 80, height: 22, color: rgb(0.94, 0.96, 1) });
      pagina.drawText('Datum', { x: 45, y, size: 10, font: fontBold, color: rgb(0.2, 0.2, 0.2) });
      pagina.drawText('Omschrijving', { x: 130, y, size: 10, font: fontBold, color: rgb(0.2, 0.2, 0.2) });
      pagina.drawText('Bedrag', { x: width - 100, y, size: 10, font: fontBold, color: rgb(0.2, 0.2, 0.2) });
      y -= 26;

      declaraties.forEach((d, i) => {
        if (i % 2 === 1) pagina.drawRectangle({ x: 40, y: y - 4, width: width - 80, height: 20, color: rgb(0.98, 0.98, 0.98) });
        pagina.drawText(formatDatum(d.datum), { x: 45, y, size: 10, font, color: rgb(0.3, 0.3, 0.3) });
        const tekst = d.omschrijving.length > 52 ? d.omschrijving.slice(0, 49) + '...' : d.omschrijving;
        pagina.drawText(tekst, { x: 130, y, size: 10, font, color: rgb(0.15, 0.15, 0.15) });
        pagina.drawText(`€ ${parseFloat(d.bedrag).toFixed(2).replace('.', ',')}`, { x: width - 100, y, size: 10, font, color: rgb(0.15, 0.15, 0.15) });
        y -= 22;
      });

      y -= 8;
      pagina.drawLine({ start: { x: 40, y }, end: { x: width - 40, y }, thickness: 1, color: rgb(0.8, 0.8, 0.8) });
      y -= 18;
      pagina.drawText('Totaal', { x: 45, y, size: 11, font: fontBold, color: rgb(0.09, 0.21, 0.37) });
      pagina.drawText(`€ ${totaal.toFixed(2).replace('.', ',')}`, { x: width - 100, y, size: 11, font: fontBold, color: rgb(0.09, 0.21, 0.37) });

      for (const r of ovRegels) {
        try {
          const blob = await downloadReceipt(r.bonnetje.key);
          const bytes = new Uint8Array(await blob.arrayBuffer());
          const isPdf = r.bonnetje.type === 'application/pdf';
          if (isPdf) {
            const subDoc = await PDFDocument.load(bytes);
            const paginas = await pdfDoc.copyPages(subDoc, subDoc.getPageIndices());
            paginas.forEach(p => pdfDoc.addPage(p));
          } else {
            let image;
            if (r.bonnetje.type === 'image/png') image = await pdfDoc.embedPng(bytes);
            else image = await pdfDoc.embedJpg(bytes);
            const imgPagina = pdfDoc.addPage([595, 842]);
            const { width: pw, height: ph } = imgPagina.getSize();
            imgPagina.drawText(`${formatDatum(r.dateString)} — OV (registratie)`, { x: 40, y: ph - 40, size: 11, font: fontBold, color: rgb(0.2, 0.2, 0.2) });
            const scale = Math.min((pw - 80) / image.width, (ph - 100) / image.height);
            imgPagina.drawImage(image, { x: (pw - image.width * scale) / 2, y: ph - image.height * scale - 60, width: image.width * scale, height: image.height * scale });
          }
        } catch { /* overslaan bij fout */ }
      }

      for (const d of declaraties) {
        if (!d.bonnetjeKey) continue;
        try {
          const blob = await downloadReceipt(d.bonnetjeKey);
          const bytes = new Uint8Array(await blob.arrayBuffer());
          if (d.bonnetjeType === 'application/pdf') {
            const subDoc = await PDFDocument.load(bytes);
            const paginas = await pdfDoc.copyPages(subDoc, subDoc.getPageIndices());
            paginas.forEach(p => pdfDoc.addPage(p));
          } else {
            let image;
            if (d.bonnetjeType === 'image/png') image = await pdfDoc.embedPng(bytes);
            else image = await pdfDoc.embedJpg(bytes);
            const imgPagina = pdfDoc.addPage([595, 842]);
            const { width: pw, height: ph } = imgPagina.getSize();
            imgPagina.drawText(`${formatDatum(d.datum)} — ${d.omschrijving}`, { x: 40, y: ph - 40, size: 11, font: fontBold, color: rgb(0.2, 0.2, 0.2) });
            const scale = Math.min((pw - 80) / image.width, (ph - 100) / image.height);
            imgPagina.drawImage(image, {
              x: (pw - image.width * scale) / 2,
              y: ph - image.height * scale - 60,
              width: image.width * scale,
              height: image.height * scale,
            });
          }
        } catch { /* bonnetje overslaan bij fout */ }
      }

      const pdfBytes = await pdfDoc.save();
      const url = URL.createObjectURL(new Blob([pdfBytes], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Declaraties_${year}-${String(month + 1).padStart(2, '0')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('PDF genereren mislukt: ' + err.message);
    } finally {
      setExportBezig(false);
    }
  };

  const formatDatum = (s) => { const [y, m, d] = s.split('-'); return `${d}-${m}-${y}`; };
  const totaal = declaraties.reduce((sum, d) => sum + (parseFloat(d.bedrag) || 0), 0);

  const ovRegels = Object.entries(ovBonnetjes?.[monthKey] || {})
    .filter(([, b]) => b?.key)
    .map(([dateString, bonnetje]) => ({
      dateString,
      bonnetje,
      bedrag: ovBedragen?.[monthKey]?.[dateString] || 0,
    }))
    .sort((a, b) => a.dateString.localeCompare(b.dateString));

  const ovTotaal = ovRegels.reduce((sum, r) => sum + (parseFloat(r.bedrag) || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Declaraties {MAANDEN[month]} {year}</h2>
          {declaraties.length > 0 && (
            <p className="text-sm text-gray-500 mt-0.5">
              {declaraties.length} post{declaraties.length !== 1 ? 'en' : ''} · totaal <span className="font-medium text-gray-700">€ {totaal.toFixed(2).replace('.', ',')}</span>
            </p>
          )}
        </div>
        <button onClick={openFormulier}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Toevoegen
        </button>
      </div>

      {toonFormulier && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-5">Declaratie toevoegen</h3>
            <form onSubmit={handleVoegToe} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Datum</label>
                <input type="date" value={datum} onChange={e => setDatum(e.target.value)}
                  min={new Date(year, month, 1).toISOString().split('T')[0]}
                  max={new Date(year, month + 1, 0).toISOString().split('T')[0]}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Categorie</label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIEEN.map(cat => (
                    <button key={cat} type="button"
                      onClick={() => setCategorie(cat)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition border ${
                        categorie === cat
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                {categorie === 'Overig' && (
                  <input type="text" value={omschrijvingOverig} onChange={e => setOmschrijvingOverig(e.target.value)}
                    placeholder="Omschrijving..."
                    className="mt-3 w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    required autoFocus
                  />
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Bedrag (€)</label>
                <input type="text" inputMode="decimal" value={bedrag} onChange={e => setBedrag(e.target.value)}
                  placeholder="0,00"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Bonnetje <span className="normal-case font-normal text-gray-400">(optioneel)</span></label>
                <input type="file" accept="image/*,application/pdf"
                  onChange={e => setBonnetje(e.target.files[0] || null)}
                  className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition"
                />
                {bonnetje && <p className="text-xs text-gray-500 mt-1">{bonnetje.name} ({(bonnetje.size / 1024).toFixed(0)} KB)</p>}
              </div>

              {uploadBezig && (
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                  Bonnetje uploaden naar S3...
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={uploadBezig || (categorie === 'Overig' && !omschrijvingOverig.trim())}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-xl transition disabled:opacity-50"
                >
                  Opslaan
                </button>
                <button type="button" onClick={sluitFormulier} disabled={uploadBezig}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-4 rounded-xl transition"
                >
                  Annuleren
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {previewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 px-4" onClick={sluitPreview}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-screen overflow-auto p-4" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm font-medium text-gray-700 truncate">{previewNaam}</p>
              <div className="flex gap-2">
                <a href={previewUrl} download={previewNaam}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium px-3 py-1.5 rounded-lg border border-blue-200 hover:bg-blue-50 transition"
                >
                  Download
                </a>
                <button onClick={sluitPreview} className="text-gray-400 hover:text-gray-600 transition px-2">✕</button>
              </div>
            </div>
            {previewNaam.toLowerCase().endsWith('.pdf')
              ? <iframe src={previewUrl} title="bonnetje" className="w-full h-96 rounded-lg border" />
              : <img src={previewUrl} alt="bonnetje" className="w-full rounded-lg" />
            }
          </div>
        </div>
      )}

      {declaraties.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-dashed border-gray-200 p-12 text-center">
          <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-gray-400 text-sm">Nog geen declaraties voor deze maand</p>
          <button onClick={openFormulier} className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium">
            Eerste declaratie toevoegen
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Datum</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Omschrijving</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Bedrag</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-14">Bon</th>
                <th className="px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {declaraties.map(d => (
                <tr key={d.id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDatum(d.datum)}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <span className="inline-flex items-center gap-2">
                      {d.omschrijving}
                      {d.categorie !== d.omschrijving && (
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{d.categorie}</span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right whitespace-nowrap">
                    € {parseFloat(d.bedrag).toFixed(2).replace('.', ',')}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {d.bonnetjeKey ? (
                      <button onClick={() => handleBekijkBonnetje(d)} title={d.bonnetjeNaam}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 transition"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleVerwijder(d)} disabled={verwijderBezig === d.id}
                      className="text-gray-300 hover:text-red-500 transition disabled:opacity-50"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {ovRegels.length > 0 && (
              <tbody className="divide-y divide-green-50">
                <tr>
                  <td colSpan="5" className="px-4 pt-3 pb-1 text-xs font-semibold text-green-700 uppercase tracking-wider bg-green-50">
                    OV-bonnetjes via Registratie
                  </td>
                </tr>
                {ovRegels.map(r => (
                  <tr key={r.dateString} className="bg-green-50/40 hover:bg-green-50 transition">
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDatum(r.dateString)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <span className="inline-flex items-center gap-2">
                        OV
                        <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">registratie</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right whitespace-nowrap">
                      {r.bedrag > 0 ? `€ ${parseFloat(r.bedrag).toFixed(2).replace('.', ',')}` : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => handleBekijkBonnetje({ bonnetjeKey: r.bonnetje.key, bonnetjeNaam: r.bonnetje.naam })}
                        title={r.bonnetje.naam}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-green-100 hover:bg-green-200 text-green-700 transition"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 text-right" title="Beheer via Registratie-pagina">🔒</td>
                  </tr>
                ))}
              </tbody>
            )}
            <tfoot>
              <tr className="bg-blue-50 border-t-2 border-blue-100">
                <td colSpan="2" className="px-4 py-3 text-sm font-bold text-blue-900">Totaal</td>
                <td className="px-4 py-3 text-sm font-bold text-blue-900 text-right">
                  € {(totaal + ovTotaal).toFixed(2).replace('.', ',')}
                </td>
                <td colSpan="2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {declaraties.length > 0 && (
        <div className="flex gap-3 flex-wrap">
          <button onClick={handleKopieerTabel}
            className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 font-medium text-sm px-4 py-2.5 rounded-lg border border-gray-300 shadow-sm transition"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            {copyToast ? 'Gekopieerd!' : 'Kopieer tabel'}
          </button>
          <button onClick={handleExportPDF} disabled={exportBezig}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm px-4 py-2.5 rounded-lg shadow-sm transition disabled:opacity-50"
          >
            {exportBezig ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
                PDF genereren...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download PDF + bonnetjes
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default Declaraties;
